import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import type { ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import { profileImageSpec } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { Segmented } from '../../../components/primitives/Segmented';
import { SpotifyHeaderFrame } from '../../studio/spotifyHeader/SpotifyHeaderFrame';
import type { HeaderPreviewGround } from '../../studio/spotifyHeader/FrameGround';
import { spotifyHeaderMessages as sh } from '../../studio/spotifyHeader/spotifyHeaderMessages';
import { ProfileToolSwitch } from './ProfileToolSwitch';
import { profileToolMessages as t } from './profileToolMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HeaderToolPreviewProps {
  ground: HeaderPreviewGround;
  colours: ThemeColours;
  watermark: boolean;
  artistName: string;
  avatarUrl: string | null;
  width: number;
}

const HEADER_WATERMARK = profileImageSpec('spotifyHeader').watermark;

/**
 * The preview of the Profile header tool (design b1, d2): the header frame of the kit Studio step,
 * Desktop or Phone, the name and the photo that Spotify draws, the crop guides, and the Free mark at
 * the place of the profile file.
 */
const HeaderToolPreviewComponent: React.FC<HeaderToolPreviewProps> = (props) => {
  const { ground, colours, watermark, artistName, avatarUrl, width } = props;
  const intl = useIntl();
  const [view, setView] = useState<'desktop' | 'phone'>('desktop');
  const [showName, setShowName] = useState(true);
  const [guides, setGuides] = useState(true);
  return (
    <div className="d-flex flex-column gap-2" data-testid="profile-tool-preview">
      <Segmented
        ariaLabel={intl.formatMessage(sh.viewLabel)}
        value={view}
        onChange={setView}
        options={[
          { value: 'desktop', label: intl.formatMessage(sh.desktop) },
          { value: 'phone', label: intl.formatMessage(sh.phone) },
        ]}
      />
      <div className={styles.railStage}>
        <SpotifyHeaderFrame
          width={view === 'phone' ? Math.min(260, width) : width}
          view={view}
          ground={ground}
          colours={colours}
          overlay={showName ? { artist: artistName, avatarUrl } : null}
          guides={guides}
          watermark={watermark}
          watermarkPlace={HEADER_WATERMARK}
          testId="profile-header-preview"
        />
      </div>
      <div className={styles.toggles}>
        <ProfileToolSwitch
          id="profile-tool-name"
          label={intl.formatMessage(sh.showName)}
          checked={showName}
          onChange={setShowName}
        />
        <ProfileToolSwitch
          id="profile-tool-guides"
          label={intl.formatMessage(t.cropGuides)}
          checked={guides}
          onChange={setGuides}
        />
      </div>
    </div>
  );
};

export const HeaderToolPreview = TypedMemo(HeaderToolPreviewComponent);
